import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Form,
  Input,
  Message,
  Modal,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography,
} from "@arco-design/web-react";
import { ArrowLeft, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useCurrentAuthUser } from "../../auth";
import { PermissionGate } from "../../../lib/auth";

import { AUTHZ_MANAGEMENT_PERMISSIONS } from "../config";
import { useAuthzRoleConfigurationRuntime } from "../runtime";
import {
  getAuthzRoleConfigurationSaveErrorMessage,
  hasNewHighRiskPermission,
  summarizeRoleDraft,
} from "../service";
import type { AuthzRoleDraft } from "../types";
import { RecentAuthorizationChanges } from "./RecentAuthorizationChanges";
import { RoleMemberTree } from "./RoleMemberTree";
import { RolePermissionTree } from "./RolePermissionTree";

/** 角色成员与权限统一配置详情页。 */
export function AuthzRoleDetailPage() {
  const navigate = useNavigate();
  const currentUserQuery = useCurrentAuthUser();
  const roleId = Number(useParams().roleId);
  const { configurationQuery, saveMutation } = useAuthzRoleConfigurationRuntime(roleId);
  const configuration = configurationQuery.data;
  const [draft, setDraft] = useState<AuthzRoleDraft | null>(null);
  const [highRiskConfirmVisible, setHighRiskConfirmVisible] = useState(false);

  useEffect(() => {
    if (!configuration) return;
    const invalidUserIds = new Set(configuration.invalidMembers.map((item) => item.userId));
    setDraft({
      version: configuration.role.version,
      name: configuration.role.name,
      description: configuration.role.description,
      userIds: configuration.selectedUserIds.filter((userId) => !invalidUserIds.has(userId)),
      permissionCodes: configuration.selectedPermissionCodes,
    });
  }, [configuration]);

  const changeSummary = useMemo(() => {
    if (!configuration || !draft) return "";
    return summarizeRoleDraft(
      configuration.selectedUserIds,
      configuration.selectedPermissionCodes,
      draft.userIds,
      draft.permissionCodes,
    );
  }, [configuration, draft]);

  if (!Number.isInteger(roleId) || roleId <= 0) return <Empty description="角色 ID 无效" />;
  if (configurationQuery.isError) return <Empty description="角色配置加载失败" />;
  if (configurationQuery.isLoading) return <Card><Skeleton text={{ rows: 8 }} animation /></Card>;
  if (!configuration || !draft) return <Empty description="角色配置加载失败" />;

  const submitDraft = async () => {
    try {
      const saved = await saveMutation.mutateAsync(draft);
      setDraft({
        version: saved.role.version,
        name: saved.role.name,
        description: saved.role.description,
        userIds: saved.selectedUserIds,
        permissionCodes: saved.selectedPermissionCodes,
      });
      Message.success("角色配置已保存");
    } catch (error) {
      Message.error(getAuthzRoleConfigurationSaveErrorMessage(error));
    }
  };

  const save = async () => {
    if (!draft.name.trim()) {
      Message.warning("角色名称不能为空");
      return;
    }
    if (
      hasNewHighRiskPermission(
        configuration.permissionTree,
        configuration.selectedPermissionCodes,
        draft.permissionCodes,
      )
    ) {
      setHighRiskConfirmVisible(true);
      return;
    }
    await submitDraft();
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%", paddingBottom: 88 }}>
      <Card>
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <Space>
              <Button icon={<ArrowLeft size={16} />} onClick={() => navigate("/settings/org-workflow/roles")}>返回角色列表</Button>
              <Typography.Title heading={5} style={{ margin: 0 }}>{configuration.role.name}</Typography.Title>
              <Tag color={configuration.role.status === "enabled" ? "green" : "gray"}>
                {configuration.role.status === "enabled" ? "已启用" : "已停用"}
              </Tag>
              {configuration.role.isSystem ? <Tag color="arcoblue">系统角色</Tag> : null}
            </Space>
            <Typography.Text type="secondary">版本 {draft.version}</Typography.Text>
          </div>
          <Descriptions
            column={3}
            data={[
              { label: "角色编码", value: configuration.role.code },
              { label: "成员", value: `${draft.userIds.length} 人` },
              { label: "权限", value: `${draft.permissionCodes.length} 项` },
            ]}
          />
          {configuration.invalidMembers.length > 0 ? (
            <Alert
              type="warning"
              title="检测到失效角色成员"
              content={(
                <div>
                  <div>以下成员已无法继续授权，已从待保存草稿中自动移除；点击“保存修改”后会正式清理角色关系：</div>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                    {configuration.invalidMembers.map((item) => (
                      <li key={item.userId}>{item.name}（用户 ID {item.userId}）：{item.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              style={{ marginBottom: 14 }}
            />
          ) : null}
          <Form layout="vertical">
            <div style={{ display: "grid", gridTemplateColumns: "minmax(240px, 1fr) minmax(360px, 2fr)", gap: 16 }}>
              <Form.Item label="角色名称" required>
                <Input aria-label="编辑角色名称" value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
              </Form.Item>
              <Form.Item label="角色说明">
                <Input aria-label="编辑角色说明" value={draft.description} onChange={(description) => setDraft({ ...draft, description })} />
              </Form.Item>
            </div>
          </Form>
        </Space>
      </Card>

      <Card>
        <Tabs defaultActiveTab="members">
          <Tabs.TabPane key="members" title={`成员配置（${draft.userIds.length}）`}>
            <RoleMemberTree
              tree={configuration.memberTree}
              selectedUserIds={draft.userIds}
              onChange={(userIds) => setDraft({ ...draft, userIds })}
            />
          </Tabs.TabPane>
          <Tabs.TabPane key="permissions" title={`权限配置（${draft.permissionCodes.length}）`}>
            <Alert
              type="info"
              content="页面权限不会自动授予其下全部操作；勾选操作会自动补齐所属页面，取消页面会清理其下操作。"
              style={{ marginBottom: 14 }}
            />
            <RolePermissionTree
              tree={configuration.permissionTree}
              selectedPermissionCodes={draft.permissionCodes}
              onChange={(permissionCodes) => setDraft({ ...draft, permissionCodes })}
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>

      <Card title="最近变更（最多两条）">
        <RecentAuthorizationChanges items={configuration.recentChanges} />
      </Card>

      <div
        style={{
          position: "fixed",
          right: 28,
          bottom: 20,
          zIndex: 20,
          display: "flex",
          alignItems: "center",
          gap: 16,
          padding: "12px 16px",
          border: "1px solid #e5e6eb",
          borderRadius: 12,
          background: "#fff",
          boxShadow: "0 8px 24px rgba(0,0,0,.12)",
        }}
      >
        <Typography.Text>{changeSummary}</Typography.Text>
        <Button onClick={() => setDraft({
          version: configuration.role.version,
          name: configuration.role.name,
          description: configuration.role.description,
          userIds: configuration.selectedUserIds.filter(
            (userId) => !configuration.invalidMembers.some((item) => item.userId === userId),
          ),
          permissionCodes: configuration.selectedPermissionCodes,
        })}>取消修改</Button>
        <PermissionGate
          user={currentUserQuery.data}
          permission={AUTHZ_MANAGEMENT_PERMISSIONS.grant}
          fallback={<Button disabled>无授权权限</Button>}
        >
          <Button type="primary" icon={<Save size={16} />} loading={saveMutation.isPending} onClick={save}>保存修改</Button>
        </PermissionGate>
      </div>

      <Modal
        title="确认授予高风险权限"
        visible={highRiskConfirmVisible}
        confirmLoading={saveMutation.isPending}
        onCancel={() => setHighRiskConfirmVisible(false)}
        onOk={async () => {
          await submitDraft();
          setHighRiskConfirmVisible(false);
        }}
      >
        {changeSummary}。本次包含高风险操作权限，请确认授权范围。
      </Modal>
    </Space>
  );
}
